// src/pages/Terms.tsx
import React from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Scale,
  Shield,
  AlertCircle,
  BookOpen,
  CheckCircle,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/terms" />

      {/* Header */}
      <section className="container mx-auto px-5 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Scale className="h-4 w-4 mr-2" />
            Legal Terms
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              {/* Introduction */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <BookOpen className="h-7 w-7 text-primary mr-3" />
                  1. Agreement to Terms
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    Welcome to ObeliskRecovery. These Terms and Conditions
                    constitute a legally binding agreement made between you,
                    whether personally or on behalf of an entity ("you") and
                    ObeliskRecovery ("we", "us", or "our"), concerning your
                    access to and use of our services.
                  </p>
                  <p>
                    By accessing or using our website, mobile application, or
                    any other services (collectively, the "Services"), you
                    acknowledge that you have read, understood, and agree to be
                    bound by these Terms. If you do not agree with all of these
                    Terms, you are prohibited from using the Services and must
                    discontinue use immediately.
                  </p>
                </div>
              </div>

              {/* Services */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Shield className="h-7 w-7 text-primary mr-3" />
                  2. Services Description
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>ObeliskRecovery provides the following services:</p>

                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        Market Protection
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Intelligent monitoring of connected crypto wallets to
                        detect market downturns and trigger protection
                        protocols.
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        Fund Recovery
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Expert recovery services for lost, stolen, or hacked
                        cryptocurrency funds through investigation and
                        negotiation.
                      </p>
                    </div>
                  </div>

                  <div
                    className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} mt-4`}
                  >
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        Important Disclaimer
                      </p>
                      <p className="text-sm mt-1">
                        ObeliskRecovery does NOT store your private keys, seed
                        phrases, or provide wallet services. Our Services
                        monitor wallet activity and provide recovery assistance
                        through external means.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Responsibilities */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  3. User Responsibilities
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>By using our Services, you agree to:</p>

                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>
                          Provide accurate and complete information during
                          registration and verification processes
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>
                          Maintain the security of your account credentials
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>
                          Use the Services only for lawful purposes and in
                          accordance with these Terms
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>
                          Notify us immediately of any unauthorized access or
                          security breaches
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>
                          Comply with all applicable laws and regulations in
                          your jurisdiction
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Fees & Payment */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  4. Fees & Payment Terms
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    4.1 Service Fees
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Market Protection
                      </h4>
                      <div className="space-y-2">
                        <p className="text-sm">
                          Monthly subscription fee:{" "}
                          <span className="font-semibold">$49.99</span>
                        </p>
                        <p className="text-sm">
                          Annual subscription fee:{" "}
                          <span className="font-semibold">$499.99</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Cancel anytime with 30-day notice
                        </p>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Recovery Services
                      </h4>
                      <div className="space-y-2">
                        <p className="text-sm">
                          Success fee:{" "}
                          <span className="font-semibold">
                            20% of recovered amount
                          </span>
                        </p>
                        <p className="text-sm">
                          Minimum fee:{" "}
                          <span className="font-semibold">$500</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Fee due only upon successful recovery
                        </p>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-8">
                    4.2 Payment Terms
                  </h3>
                  <p>
                    All fees are exclusive of applicable taxes. You are
                    responsible for any taxes, duties, or other governmental
                    assessments associated with your use of our Services.
                  </p>

                  <div
                    className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo}`}
                  >
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Refund Policy</p>
                      <p className="text-sm mt-1">
                        Subscription fees for Market Protection services are
                        non-refundable. Recovery services operate on a
                        success-fee basis only.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimers & Limitations */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  5. Disclaimers & Limitations of Liability
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    5.1 Service Disclaimers
                  </h3>
                  <p>
                    OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                    WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE:
                  </p>

                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>100% success rate in recovering lost funds</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>
                          Complete protection against all market downturns
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>Immediate recovery of lost funds</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>
                          Protection against all security threats or hacks
                        </span>
                      </li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-8">
                    5.2 Limitation of Liability
                  </h3>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, OBELISKRECOVERY
                    SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                    CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT
                    LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER
                    INTANGIBLE LOSSES, RESULTING FROM:
                  </p>

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 mt-4">
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>Your use or inability to use the Services</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>
                          Any unauthorized access to or use of your account
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>
                          Any interruption or cessation of transmission to or
                          from our Services
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>
                          Any errors or omissions in any content or for any loss
                          or damage incurred
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  6. Intellectual Property Rights
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    The Services and their original content, features, and
                    functionality are and will remain the exclusive property of
                    ObeliskRecovery and its licensors. Our trademarks and trade
                    dress may not be used in connection with any product or
                    service without the prior written consent of
                    ObeliskRecovery.
                  </p>
                  <p>
                    You are granted a limited, non-exclusive, non-transferable,
                    and revocable license to access and use the Services for
                    their intended purpose.
                  </p>
                </div>
              </div>

              {/* Termination */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  7. Termination
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    We may terminate or suspend your account and access to our
                    Services immediately, without prior notice or liability, for
                    any reason whatsoever, including without limitation if you
                    breach these Terms.
                  </p>
                  <p>
                    Upon termination, your right to use the Services will
                    immediately cease. All provisions of these Terms which by
                    their nature should survive termination shall survive
                    termination, including, without limitation, ownership
                    provisions, warranty disclaimers, indemnity, and limitations
                    of liability.
                  </p>
                </div>
              </div>

              {/* Governing Law */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  8. Governing Law
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    These Terms shall be governed and construed in accordance
                    with the laws of the State of Delaware, United States,
                    without regard to its conflict of law provisions.
                  </p>
                  <p>
                    Our failure to enforce any right or provision of these Terms
                    will not be considered a waiver of those rights. If any
                    provision of these Terms is held to be invalid or
                    unenforceable by a court, the remaining provisions of these
                    Terms will remain in effect.
                  </p>
                </div>
              </div>

              {/* Changes to Terms */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  9. Changes to These Terms
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  We reserve the right, at our sole discretion, to modify or
                  replace these Terms at any time. If a revision is material, we
                  will provide at least 30 days' notice prior to any new terms
                  taking effect. What constitutes a material change will be
                  determined at our sole discretion.
                </p>
                <p className="text-gray-600 dark:text-gray-400 mt-4">
                  By continuing to access or use our Services after those
                  revisions become effective, you agree to be bound by the
                  revised terms.
                </p>
              </div>

              {/* Contact */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  10. Contact Information
                </h2>
                <div className="bg-primary/5 rounded-xl p-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    If you have any questions about these Terms, please contact
                    us at:
                  </p>
                  <div className="space-y-2">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Legal Department: legal@obeliskrecovery.com
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Address: 123 Legal Avenue, Wilmington, Delaware 19801, USA
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                      For legal notices, please use the mailing address above.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="ObeliskRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  ObeliskRecovery
                </span>
              </div>
              <p className="mb-4">
                Secure your crypto assets against market downturns and recover
                lost funds with expert assistance.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/wallet" className="hover:text-white">
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link to="/recovery" className="hover:text-white">
                    Fund Recovery
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="hover:text-white">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2">support@obeliskrecovery.com</p>
              <p>24/7 Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p>
              © {new Date().getFullYear()} ObeliskRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
